import { Button } from '@/components/custom/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/custom/label'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm, useWatch, useFieldArray } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { AttributeFieldType } from '@/constants/setting'
import SelectFieldType from '../../ticket/attribute/select-field-type'
import { createAttribute, editAttribute } from '@/api/settings'
import { toast } from 'sonner'
import { throwException } from '@/exceptions/throw-exception.js'
import { uniq } from 'lodash-es'
import ButtonClose from '@/components/custom/button-close'
import { useCustomerListFilterStore } from '@/store/customer-list-filter-store'
import QUERY_KEY from '@/constants/use-query-key.js'

const formSchema = z
  .object({
    title: z
      .string()
      .nonempty({ message: 'Tên thuộc tính không được bỏ trống' }),
    key: z.string().nonempty({ message: 'Key thuộc tính không được bỏ trống' }),
    type: z.string().nonempty({ message: 'Kiểu dữ liệu không được bỏ trống' }),
    options: z.array(z.string()).optional(),
    isRequired: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.title.trim()) {
      ctx.addIssue({
        path: ['title'],
        message: 'Tên thuộc tính không được bỏ trống',
      })
    }

    if (!data.key.trim()) {
      ctx.addIssue({
        path: ['key'],
        message: 'Key thuộc tính không được bỏ trống',
      })
    }

    if (!/^[a-zA-Z_]+$/.test(data.key.trim())) {
      ctx.addIssue({
        path: ['key'],
        message: 'Key thuộc tính chỉ nhập chữ cái và dấu "_"',
      })
    }

    if (!data.type.trim()) {
      ctx.addIssue({
        path: ['type'],
        message: 'Kiểu dữ liệu không được bỏ trống',
      })
    }

    if (
      data.type === AttributeFieldType.Select ||
      data.type === AttributeFieldType.MultiSelect
    ) {
      if (!data.options.length) {
        ctx.addIssue({
          path: ['type'],
          message: 'Nhập giá trị cho các lựa chọn',
        })
      }

      const isAllNonEmpty = data.options.every((x) => !!x.trim())
      if (!isAllNonEmpty) {
        ctx.addIssue({
          path: ['type'],
          message: 'Nhập giá trị cho các lựa chọn',
        })
      }

      const uniqueOptions = uniq(data.options.map((x) => x.trim()))
      if (uniqueOptions.length !== data.options.length) {
        ctx.addIssue({
          path: ['type'],
          message: 'Tồn tại giá trị trùng nhau',
        })
      }
    }
  })

export default function DialogCustomerAttribute({
  open,
  customerAttribute,
  handleClose,
}) {
  const queryClient = useQueryClient()
  const formRef = useRef(null)
  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      title: '',
      key: '',
      type: '',
      options: [],
      isRequired: false,
    },
  })

  const fieldType = useWatch({
    control: form.control,
    name: 'type',
  })

  const {
    fields: selectOptions,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: 'options',
  })

  const createAttributeMutation = useMutation({
    mutationFn: createAttribute,
    onSuccess: () => {
      toast.success('Tạo mới thuộc tính khách hàng thành công')
      useCustomerListFilterStore.getState().setRefreshField(new Date())
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_LIST_USER_ATTRIBUTES],
      })

      handleClose()
    },
    onError: throwException,
  })

  const editAttributeMutation = useMutation({
    mutationFn: editAttribute,
    onSuccess: () => {
      toast.success('Cập nhật thuộc tính khách hàng thành công')
      useCustomerListFilterStore.getState().setRefreshField(new Date())
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_LIST_USER_ATTRIBUTES],
      })

      handleClose()
    },
    onError: throwException,
  })

  const handleCreateCustomerAttribute = (payload) => {
    createAttributeMutation.mutate({ data: payload })
  }

  const handleEditCustomerAttribute = (customerAttributeId, payload) => {
    editAttributeMutation.mutate({
      id: customerAttributeId,
      data: payload,
    })
  }

  const buildPayload = (fieldsValue) => {
    const payload = {}

    const payloadFields = {
      target: 'user',
      key: fieldsValue.key.trim(),
      title: fieldsValue.title.trim(),
      type: fieldsValue.type,
      required: fieldsValue.isRequired,
    }

    if (
      fieldsValue.type === AttributeFieldType.Select ||
      fieldsValue.type === AttributeFieldType.MultiSelect
    ) {
      payloadFields.data = fieldsValue.options
    }

    payload.payloadFields = payloadFields
    payload.payloadTemplatePermission = {}

    return payload
  }

  const onSubmit = (fieldsValue) => {
    const payload = buildPayload(fieldsValue)
    if (!customerAttribute) {
      handleCreateCustomerAttribute(payload)
    } else {
      handleEditCustomerAttribute(customerAttribute._id, payload)
    }
  }

  useEffect(() => {
    if (customerAttribute) {
      const fieldsValue = {
        title: customerAttribute.title,
        key: customerAttribute.key,
        type: customerAttribute.type,
        options: customerAttribute.data ?? [],
        isRequired: customerAttribute.required ?? false,
      }

      form.reset(fieldsValue)
    }
  }, [customerAttribute, form])

  return (
    <Dialog open={open} onOpenChange={() => handleClose()}>
      <DialogContent className="max-h-[640px] overflow-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {!customerAttribute
              ? 'Thêm mới thuộc tính khách hàng'
              : 'Cập nhật thuộc tính khách hàng'}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription
          className="hidden"
          aria-describedby="attribute-dialog"
        />
        <Form {...form}>
          <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <Label isRequired>Tên thuộc tính</Label>
                    <FormControl>
                      <Input {...field} placeholder="Tên thuộc tính" />
                    </FormControl>
                    {form.formState.errors && form.formState.errors.title && (
                      <span className="text-sm text-[--label-red-foreground]">
                        {form.formState.errors.title.message}
                      </span>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <Label isRequired>Key thuộc tính</Label>
                    <FormControl>
                      <Input {...field} placeholder="Key thuộc tính" />
                    </FormControl>
                    {form.formState.errors && form.formState.errors.key && (
                      <span className="text-sm text-[--label-red-foreground]">
                        {form.formState.errors.key.message}
                      </span>
                    )}
                  </FormItem>
                )}
              />

              <p className="text-xs text-gray-400">
                Các key thuộc tính không được trùng với các key thuộc tính mặc
                định: __v, _id, access, admin, agent_id, avaiable_status,
                address, city_id, created_at, district_id, email, facebook,
                gender, group_id, is_online, name, note, organize_id,
                ott_user_id, password, phone, phonesub, role, rule_chat, source,
                sub_phone, tags, tenant_id, updated_at, username, active, avatar
              </p>

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <Label isRequired>Kiểu dữ liệu</Label>
                    <FormControl>
                      <SelectFieldType
                        {...field}
                        onChange={(value) => {
                          field.onChange(value)

                          if (
                            (value === AttributeFieldType.Select ||
                              value === AttributeFieldType.MultiSelect) &&
                            !selectOptions.length
                          ) {
                            append('')
                          }
                        }}
                      />
                    </FormControl>
                    {form.formState.errors && form.formState.errors.type && (
                      <span className="text-sm text-[--label-red-foreground]">
                        {form.formState.errors.type.message}
                      </span>
                    )}
                  </FormItem>
                )}
              />

              {(fieldType === 'select' || fieldType === 'multi-select') && (
                <FormItem className="space-y-2">
                  <Label>Giá trị lựa chọn</Label>
                  {selectOptions.map((option, index) => (
                    <Controller
                      key={option.id}
                      name={`options.${index}`}
                      control={form.control}
                      render={({ field }) => (
                        <div className="space-y-1">
                          <Label isRequired>Lựa chọn {index + 1}</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              {...field}
                              className="flex-1"
                              placeholder={`Lựa chọn ${index + 1}`}
                            />

                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => remove(index)}
                            >
                              <Trash2 className="h-4 w-4 cursor-pointer text-[hsl(var(--icon-danger))]" />
                            </Button>
                          </div>
                        </div>
                      )}
                    />
                  ))}

                  <div className="flex w-full items-center">
                    <Button type="button" onClick={() => append('')}>
                      Thêm lựa chọn
                    </Button>
                  </div>
                </FormItem>
              )}

              <FormField
                control={form.control}
                name="isRequired"
                render={({ field }) => (
                  <FormItem className="flex items-end gap-2">
                    <Label>Bắt buộc</Label>
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
        <DialogFooter className="sm:justify-right">
          <DialogClose asChild>
            <ButtonClose onClose={handleClose} />
          </DialogClose>
          <Button type="submit" onClick={() => formRef.current.requestSubmit()}>
            {!customerAttribute ? 'Thêm mới' : 'Cập nhật'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
